import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../residents/components/FilterPopover.jsx';
import AreaEmptyState from './AreaEmptyState.jsx';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';

/**
 * Column definitions for the areas table.
 * Each column has: key, label, sortable, filterable, required (can't hide).
 */
export const COLUMN_DEFS = [
  { key: 'area_name', label: 'Area Name', sortable: true, filterable: true, required: true },
  { key: 'area_type_description', label: 'Area Type', sortable: true, filterable: true, required: false },
  { key: 'area_group_name', label: 'Area Group', sortable: false, filterable: true, required: false },
  { key: 'building_name', label: 'Building', sortable: true, filterable: true, required: false },
  { key: 'max_occupancy', label: 'Max Occupancy', sortable: true, filterable: true, required: false },
  { key: 'residents', label: 'Residents', sortable: false, filterable: false, required: false },
  { key: 'devices', label: 'Devices', sortable: false, filterable: true, required: false },
  { key: 'features', label: 'Features', sortable: false, filterable: true, required: false },
];

// Small inline icons for the feature chips (flat V2 stroke style).
const FeatureIcon = ({ paths }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
       style={{ flexShrink: 0 }}>
    {paths.map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const FEATURE_PHONE_ICON = <FeatureIcon paths={['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.74a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z']} />;
const FEATURE_SHIELD_ICON = <FeatureIcon paths={['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4']} />;
const FEATURE_BELL_ICON = <FeatureIcon paths={['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0']} />;

// E-call (nurse call) device categories — a room with any of these has e-call.
const ECALL_CATEGORY_IDS = new Set([1, 2, 6, 11, 15, 16]);

/**
 * Feature definitions for the Features column — the single source of truth for
 * detection, chip rendering, filter options, and filter matching. Each `test`
 * receives (row, ctx) where ctx = { isGrandstream, roomCallingAreaIds,
 * fallProtectionAreaIds }.
 */
export const FEATURE_DEFS = [
  {
    key: 'e-call', label: 'E-Call', tone: 'primary', icon: FEATURE_BELL_ICON,
    title: 'This room has fixed nurse call (e-call) devices.',
    test: (row) => (row.devices || []).some((d) => ECALL_CATEGORY_IDS.has(parseInt(d?.device_category_id, 10))),
  },
  {
    key: 'room-calling', label: 'Room Calling', tone: 'info', icon: FEATURE_PHONE_ICON,
    title: 'This room has a Grandstream speaker for room calling.',
    test: (row, ctx) => !!ctx.isGrandstream && !!ctx.roomCallingAreaIds?.has(Number(row.area_id)),
  },
  {
    key: 'fall-protection', label: 'Fall Protection', tone: 'success', icon: FEATURE_SHIELD_ICON,
    title: 'This room has SafelyYou fall protection.',
    test: (row, ctx) => !!ctx.fallProtectionAreaIds?.has(Number(row.area_id)),
  },
];

/** Returns the feature defs that apply to an area, in display order. */
export function getAreaFeatures(row, ctx = {}) {
  return FEATURE_DEFS.filter((f) => f.test(row, ctx));
}

/**
 * AreasTable — full data table for areas with sorting, filtering, sticky header,
 * row selection, row actions, and consistent row heights.
 */
const AreasTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onEditArea,
  onManageDevices,
  onDeleteArea,
  onAddArea,
  onClearFilters,
  canModifyAreas = true,
  getDeleteBlockedReason,
  isGrandstream = false,
  roomCallingAreaIds,
  fallProtectionAreaIds,
}) => {
  const activeColumns = useMemo(
    () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
    [visibleColumns]
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = useCallback(
    (columnKey) => {
      const values = new Set();
      (allData || data).forEach((row) => {
        if (columnKey === 'area_group_name') {
          // areas can belong to multiple groups
          const groups = row.area_groups || [];
          if (groups.length === 0) {
            values.add('(empty)');
          } else {
            groups.forEach((g) => values.add(g.area_group_name || g));
          }
        } else if (columnKey === 'devices') {
          // Filter on the device name shown in the popover
          // (device_name with description/descriptions fallback).
          const devs = row.devices || [];
          if (devs.length === 0) {
            values.add('(empty)');
          } else {
            devs.forEach((d) => {
              const name = d.device_name || d.description || d.descriptions;
              if (name) values.add(name);
            });
          }
        } else if (columnKey === 'features') {
          const feats = getAreaFeatures(row, { isGrandstream, roomCallingAreaIds, fallProtectionAreaIds });
          if (feats.length === 0) {
            values.add('(empty)');
          } else {
            feats.forEach((f) => values.add(f.label));
          }
        } else {
          const val = row[columnKey];
          values.add(val != null && val !== '' ? String(val) : '(empty)');
        }
      });
      return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    },
    [allData, data, isGrandstream, roomCallingAreaIds, fallProtectionAreaIds]
  );

  const allSelected = data.length > 0 && data.every((r) => selectedIds.includes(r.area_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
      </svg>
    ) : (
      <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'area_name':
        return (
          <Link to={`/community/areas/${row.area_id}`} className="ap-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)', textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        );

      case 'area_type_description':
        return row.area_type_description ? (
          <span className="ap-area-type-tag" title={row.area_type_description}>
            {row.area_type_description}
          </span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'area_group_name':
        return <AreaGroupsCell groups={row.area_groups || []} />;

      case 'building_name':
        return row.building_name ? (
          <span className="ap-cell-text">{row.building_name}</span>
        ) : (
          <span className="ap-cell-text--muted">—</span>
        );

      case 'max_occupancy':
        return <span className="ap-cell-text">{row.max_occupancy != null ? row.max_occupancy : '—'}</span>;

      case 'residents': {
        const residents = row.residents || [];
        if (residents.length === 0) return <span className="ap-cell-text--muted">&mdash;</span>;
        return (
          <div className="ap-resident-list">
            {residents.map((r, i) => {
              const name = r.resident_name || `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim();
              return (
                <Link
                  key={r.resident_id || i}
                  to={`/community/residents/${r.resident_id}`}
                  className="ap-resident-badge"
                >
                  {name}
                </Link>
              );
            })}
          </div>
        );
      }

      case 'devices': {
        const devices = row.devices || [];
        // "None" carries the same action as the badge so a first device can be
        // assigned from here rather than via another route.
        if (devices.length === 0) {
          return (
            <NoneActionCell
              className="ap-cell-text--muted"
              onClick={onManageDevices ? () => onManageDevices(row) : undefined}
              title="Click to manage devices"
            />
          );
        }
        return <DevicesPopoverCell devices={devices} onClick={() => onManageDevices?.(row)} />;
      }

      case 'features': {
        const chips = getAreaFeatures(row, { isGrandstream, roomCallingAreaIds, fallProtectionAreaIds });
        if (chips.length === 0) return <span className="ap-cell-text--muted">—</span>;
        return (
          <div className="ap-feature-chips">
            {chips.map((c) => (
              <FeatureChip key={c.key} chip={c} />
            ))}
          </div>
        );
      }

      default:
        return <span className="ap-cell-text">{row[columnKey] || '—'}</span>;
    }
  };

  if (data.length === 0) {
    const hasAnyData = (allData || []).length > 0;
    return (
      <AreaEmptyState
        type={hasAnyData ? 'no-results' : 'no-data'}
        onAddArea={hasAnyData ? undefined : onAddArea}
        onClearFilters={hasAnyData ? onClearFilters : undefined}
      />
    );
  }

  return (
    <div className="ap-table-wrap">
      <table className="ap-table">
        <thead className="ap-thead">
          <tr>
            {/* Checkbox column */}
            <th scope="col" className="ap-th ap-th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onSelectAll}
                className="ap-checkbox"
                aria-label="Select all areas"
              />
            </th>

            {/* Actions column */}
            <th scope="col" className="ap-th ap-th--actions">Edit</th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="ap-th">
                <div className="ap-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="ap-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.key}
                      label={col.label}
                      values={getFilterValues(col.key)}
                      selectedValues={filters[col.key] || []}
                      onChange={onFilterChange}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="ap-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.area_id);
            return (
              <tr
                key={row.area_id}
                className={`ap-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                <td className="ap-td ap-td--checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelectRow(row.area_id)}
                    className="ap-checkbox"
                    aria-label={`Select ${row.area_name}`}
                  />
                </td>

                {/* Row actions kebab */}
                <td className="ap-td ap-td--actions">
                  <AreaRowActions
                    area={row}
                    onEdit={onEditArea}
                    onManageDevices={onManageDevices}
                    onDelete={onDeleteArea}
                    canModifyAreas={canModifyAreas}
                    deleteBlockedReason={getDeleteBlockedReason ? getDeleteBlockedReason(row) : null}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="ap-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * FeatureChip — compact icon-only capability badge with a hover popover that
 * names the feature (label) and describes it (title).
 */
const FeatureChip = ({ chip }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popW = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - popW / 2, window.innerWidth - popW - 8));
    setStyle({
      position: 'fixed', zIndex: 9999, left: `${left}px`, width: `${popW}px`,
      top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
    });
    setOpen(true);
  };

  return (
    <span
      ref={ref}
      className={`ap-feature-chip ap-feature-chip--${chip.tone}`}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
      aria-label={`${chip.label} — ${chip.title}`}
    >
      {chip.icon}
      {open ? (
        <span className="ap-feature-popover" style={style} role="tooltip">
          <span className="ap-feature-popover__title">{chip.label}</span>
          <span className="ap-feature-popover__text">{chip.title}</span>
        </span>
      ) : null}
    </span>
  );
};

/**
 * DevicesPopoverCell — hover popover showing device details for an area.
 * Clicking the badge opens the device manager drawer.
 */
const DevicesPopoverCell = ({ devices, onClick }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  const computePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popW = 300;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - popW / 2, window.innerWidth - popW - 8));
    setPopoverStyle({
      position: 'fixed', zIndex: 9999, left: `${left}px`, width: `${popW}px`,
      top: spaceBelow < 150 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 150 ? `${window.innerHeight - rect.top + 6}px` : undefined,
    });
  };

  const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
  const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

  const handleClick = () => {
    setOpen(false);
    if (onClick) onClick();
  };

  return (
    <div className="ap-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        type="button"
        className="ap-device-badge"
        onClick={handleClick}
        aria-label={`${devices.length} device${devices.length !== 1 ? 's' : ''} assigned`}
      >
        <i className="fe fe-cpu" style={{ fontSize: '12px' }} aria-hidden="true" />
        {devices.length}
      </button>
      {open ? (
        <div className="ap-device-popover" style={popoverStyle}>
          <div className="ap-device-popover__title">Devices ({devices.length})</div>
          <ul className="ap-device-popover__list">
            {devices.map((d, i) => (
              <li key={d.device_id || i} className="ap-device-popover__item" style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                <span className="ap-device-popover__name">
                  {d.device_name || d.description || d.descriptions}
                </span>
                {d.inovonics_model_name ? (
                  <span className="ap-device-popover__uuid">{d.inovonics_model_name}</span>
                ) : null}
                {d.descriptions ? (
                  <span className="ap-device-popover__uuid">{d.descriptions}</span>
                ) : null}
                {d.manufacture_uuid ? (
                  <span className="ap-device-popover__uuid">{d.manufacture_uuid}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * AreaGroupsCell — inline pill tags for each area group name.
 */
const AreaGroupsCell = ({ groups }) => {
  if (!groups || groups.length === 0) {
    return <span className="ap-cell-text--muted">&mdash;</span>;
  }

  return (
    <div className="ap-group-pills">
      {groups.map((g, i) => (
        <span key={g.area_group_id || i} className="ap-group-pill">
          {g.area_group_name || g}
        </span>
      ))}
    </div>
  );
};

/**
 * AreaRowActions — kebab menu for per-row actions.
 */
const AreaRowActions = ({ area, onEdit, onManageDevices, onDelete, canModifyAreas = true, deleteBlockedReason = null }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

  return (
    <div className="ap-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="ap-row-actions__trigger"
        aria-label={`Actions for ${area.area_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="ap-row-actions__menu" style={menuStyle}>
          <Link
            to={`/community/areas/${area.area_id}`}
            className="ap-row-actions__item"
            style={{ textDecoration: 'none' }}
            onClick={() => setOpen(false)}
          >
            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            View Profile
          </Link>
          <button
            type="button"
            onClick={() => { onEdit(area); setOpen(false); }}
            className="ap-row-actions__item"
          >
            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
            {canModifyAreas ? 'Edit Area' : 'Edit Area Groups'}
          </button>
          <button
            type="button"
            onClick={() => { onManageDevices(area); setOpen(false); }}
            className="ap-row-actions__item"
          >
            <i className="fe fe-cpu ap-row-actions__item-icon" style={{ fontSize: '14px' }} aria-hidden="true" />
            Manage Devices
          </button>
          {canModifyAreas ? (
            /* Blocked, not `disabled`. AreasPage.handleDeleteArea already answers
               "why can't I delete this?" with a toast, but a disabled button
               swallows the click so that explanation never fired — the user was
               left with only a hover tooltip. aria-disabled keeps it announced as
               unavailable while letting the click through to explain itself.
               (It also drops the grey block the global `button:disabled` in
               custom.css:400 was painting behind the row.) */
            <button
              type="button"
              onClick={() => { onDelete(area); setOpen(false); }}
              className={`ap-row-actions__item ap-row-actions__item--danger${deleteBlockedReason ? ' is-blocked' : ''}`}
              aria-disabled={deleteBlockedReason ? true : undefined}
              title={deleteBlockedReason || undefined}
            >
              <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
              Delete Area
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AreasTable;
